"use client";

import { ScrollShadows } from "@/components/ui/scroll-area";
import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

export interface IErrorToastContext {
  "setToastError": (error: unknown) => void;
}

export const ErrorToastContext = createContext<IErrorToastContext>({
  "setToastError": () => null,
});

/**
 * Renders the error toast body with scroll shadows when content overflows.
 *
 * @param props - Error lines to display.
 * @returns Toast content element.
 */
function ErrorToastContent({ lines }: { lines: string[] }): React.ReactElement {
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative" style={{ wordBreak: "break-word" }}>
      <div ref={scrollRef} className="flex flex-col gap-2 max-h-[350px] overflow-y-auto">
        <div className="flex items-center justify-between">
          <strong className="text-orange-600">Error</strong>
        </div>
        <div>
          {lines.filter((el) => el).splice(-1)}
          {lines.length > 1 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                More details
              </summary>
              <div className="mt-2 space-y-1">
                {lines.map((line, i) => (
                  <p
                    key={i}
                    className={lines.length - 1 === i ? "pb-1" : ""}
                  >
                    {line}
                  </p>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
      <ScrollShadows scrollRef={scrollRef} />
    </div>
  );
}

/**
 * Builds an error toast to display error messages.
 *
 * @param params - Parameters passed from the parent component.
 *
 * @returns The error toast.
 */
export default function ErrorToast({
  children,
}: {
  "children": React.ReactNode;
}): React.ReactElement {
  const toastRef = useRef<number | string | null>(null);
  const [toastError, setFormattedToastError] = useState<string | null>(null);
  const errLines = (toastError ?? "").split("\n").filter(Boolean);

  const setToastError = useCallback((error: unknown): void => {
    if (error !== null) {
      console.error(error);
    }

    if (error instanceof Error) {
      setFormattedToastError(error.message);
    } else if (error === null) {
      setFormattedToastError(null);
    } else {
      setFormattedToastError(String(error));
    }
  }, []);

  useEffect(() => {
    if (toastError !== null) {
      // Show toast with Sonner
      toastRef.current = toast.error(
        <ErrorToastContent lines={errLines} />,
        {
          duration: Infinity,
          onDismiss: () => {
            // Resetting the error state on toast close is necessary to force
            // the toast to display again, even if the underlying error message
            // hasn't been updated. This prevents the toast from remaining hidden
            // if the error condition persists.
            setToastError(null);
            toastRef.current = null;
          },
        }
      );
    } else if (toastRef.current !== null) {
      // Hide toast
      toast.dismiss(toastRef.current);
      toastRef.current = null;
    }
  }, [toastError, errLines, setToastError]);

  // Resetting the error state on toast close is necessary to force
  // the toast to display again, even if the underlying error message
  // hasn't been updated. This prevents the toast from remaining hidden
  // if the error condition persists.
  useEffect(() => {
    return () => {
      if (toastRef.current !== null) {
        toast.dismiss(toastRef.current);
      }
    };
  }, []);

  return (
    <ErrorToastContext.Provider value={{ setToastError }}>
      {children}
    </ErrorToastContext.Provider>
  );
}
